import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  addFollowUpReminderApi,
  addRelationshipRecordedNoteApi,
  deleteFollowUpReminderApi,
  deleteRelationshipRecordedNoteApi,
  fetchFollowUpReminderApi,
  fetchRelationshipMappingApi,
  fetchRelationshipMappingDetailApi,
  getRelationshipRecordedNotesApi,
  updateFollowUpReminderApi,
  updateRelationshipRecordedNoteApi,
  updateRelationshipTypeApi,
  moveContactToCampaignApi,
  draftRelationshipEmailApi,
  findRelationshipEmailApi,
  sendRelationshipEmailApi,
  sendAccountEmailApi,
  saveRelationshipContactDetailsApi,
} from "../api/accounts-relationshipApi";
import { showErrorFromMutation, showSuccess, showError } from "@/shared/utils/toast";


// relationship detail key
const relationshipDetailKey = (relationshipMappingId) => [
  "relationshipMappingDetail",
  relationshipMappingId,
];

const relationshipNotesKey = (relationshipMappingId) => [
  "relationshipRecordedNotes",
  relationshipMappingId,
];

const followUpReminderKey = (relationshipMappingId) => [
  "followUpReminder",
  relationshipMappingId,
];

// fetch relationship mapping
const relationshipMappingKey = (companyName, searchQuery = "") => [
  "relationshipMapping",
  companyName,
  searchQuery,
];

export const useFetchAccountRelationship = ({ companyName = "", searchQuery = "" }) => {
  return useQuery({
    queryKey: relationshipMappingKey(companyName, searchQuery),
    queryFn: () => fetchRelationshipMappingApi({ companyName, searchQuery }),
    enabled: !!companyName,
    placeholderData: keepPreviousData,
  });
};

export const useRefreshAccountRelationship = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyName, searchQuery = "" }) =>
      fetchRelationshipMappingApi({
        companyName,
        searchQuery,
        forceRefresh: true,
      }),
    onSuccess: (data, { companyName, searchQuery = "" }) => {
      queryClient.setQueryData(relationshipMappingKey(companyName, searchQuery), data);
      queryClient.invalidateQueries({
        queryKey: relationshipMappingKey(companyName, searchQuery),
      });
      showSuccess("Relationship mapping refreshed");
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to refresh relationship mapping",
      );
    },
  });
};


// get relationship mapping detail
export const useRelationshipMappingDetail = ({
  relationshipMappingId = "",
  enabled = true,
}) => {
  return useQuery({
    queryKey: relationshipDetailKey(relationshipMappingId),
    queryFn: () =>
      fetchRelationshipMappingDetailApi({
        relationshipMappingId,
      }),
    enabled: enabled && !!relationshipMappingId,
    staleTime: 60 * 1000,
  });
};

export const useRefreshRelationshipMappingDetail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ relationshipMappingId }) =>
      fetchRelationshipMappingDetailApi({
        relationshipMappingId,
        forceRefresh: true,
      }),
    onSuccess: (data, { relationshipMappingId }) => {
      queryClient.setQueryData(relationshipDetailKey(relationshipMappingId), data);
      queryClient.invalidateQueries({
        queryKey: relationshipDetailKey(relationshipMappingId),
      });
      queryClient.invalidateQueries({
        queryKey: ["relationshipMapping"],
      });
      showSuccess("Contact details refreshed");
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to refresh contact details",
      );
    },
  });
};

export const useSaveRelationshipContactDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveRelationshipContactDetailsApi,
    onSuccess: (data, { relationshipMappingId }) => {
      queryClient.setQueryData(
        relationshipDetailKey(relationshipMappingId),
        (current) =>
          current
            ? {
                ...current,
                contact_id: data.contact_id,
                crm_contact: data.crm_contact,
              }
            : current,
      );
      queryClient.invalidateQueries({ queryKey: ["relationshipMapping"] });
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to save contact details",
      );
    },
  });
};

export const useDraftRelationshipEmail = () =>
  useMutation({
    mutationFn: ({ relationshipMappingId }) =>
      draftRelationshipEmailApi({ relationshipMappingId }),
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to draft email");
    },
  });

export const useFindRelationshipEmail = () =>
  useMutation({
    mutationFn: findRelationshipEmailApi,
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to find a work email",
      );
    },
  });

export const useSendRelationshipEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendRelationshipEmailApi,
    onSuccess: (_data, { relationshipMappingId }) => {
      showSuccess("Email sent successfully");
      queryClient.invalidateQueries({
        queryKey: relationshipDetailKey(relationshipMappingId),
      });
      queryClient.invalidateQueries({ queryKey: ["accountInteractions"] });
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to send email");
    },
  });
};

export const useSendAccountEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendAccountEmailApi,
    onSuccess: () => {
      showSuccess("Email sent successfully");
      queryClient.invalidateQueries({ queryKey: ["accountInteractions"] });
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to send email");
    },
  });
};


// update relationship type
export const useUpdateRelationshipType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ relationshipMappingId, relationshipType }) =>
      updateRelationshipTypeApi({ relationshipMappingId, relationshipType }),
    onMutate: async ({ relationshipMappingId, relationshipType }) => {
      const detailKey = relationshipDetailKey(relationshipMappingId);

      await queryClient.cancelQueries({ queryKey: detailKey });

      const previousDetail = queryClient.getQueryData(detailKey);

      queryClient.setQueryData(detailKey, (current) =>
        current
          ? { ...current, user_relationship_status: relationshipType }
          : current,
      );

      return { previousDetail, relationshipMappingId };
    },
    onSuccess: (data, { relationshipMappingId }) => {
      const detailKey = relationshipDetailKey(relationshipMappingId);

      queryClient.setQueryData(detailKey, (current) =>
        current
          ? {
              ...current,
              user_relationship_status:
                data?.user_relationship_status ??
                current.user_relationship_status,
              updated_at: data?.updated_at ?? current.updated_at,
            }
          : current,
      );

      showSuccess("Relationship updated");
    },
    onError: (error, _variables, context) => {
      if (context?.relationshipMappingId) {
        queryClient.setQueryData(
          relationshipDetailKey(context.relationshipMappingId),
          context.previousDetail,
        );
      }

      showErrorFromMutation(
        error,
        error.message || "Failed to update relationship",
      );
    },
    onSettled: (_data, _error, variables) => {
      if (!variables?.relationshipMappingId) return;

      queryClient.invalidateQueries({
        queryKey: relationshipDetailKey(variables.relationshipMappingId),
      });
    },
  });
};


// get relationship recorded notes
export const useGetRelationshipRecordedNotes = ({
  relationshipMappingId = "",
}) => {
  return useQuery({
    queryKey: relationshipNotesKey(relationshipMappingId),
    queryFn: () => getRelationshipRecordedNotesApi({ relationshipMappingId }),
    enabled: !!relationshipMappingId,
  });
};


// add relationship recorded note
export const useAddRelationshipRecordedNote = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ relationshipMappingId, note }) =>
      addRelationshipRecordedNoteApi({ relationshipMappingId, note }),

    onSuccess: (_data, { relationshipMappingId }) => {
      showSuccess("Note added successfully!");
      queryClient.invalidateQueries({
        queryKey: relationshipNotesKey(relationshipMappingId),
      });
      action?.();
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to add note");
    },
  });
};

// update relationship recorded note
export const useUpdateRelationshipRecordedNote = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, note }) =>
      updateRelationshipRecordedNoteApi({ noteId, note }),

    onSuccess: (_data, { relationshipMappingId }) => {
      showSuccess("Note updated successfully!");
      queryClient.invalidateQueries({
        queryKey: relationshipNotesKey(relationshipMappingId),
      });
      action?.();
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to update note");
    },
  });
};

// delete relationship recorded note
export const useDeleteRelationshipRecordedNote = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId }) => deleteRelationshipRecordedNoteApi({ noteId }),

    onMutate: async ({ noteId, relationshipMappingId }) => {
      const notesKey = relationshipNotesKey(relationshipMappingId);

      await queryClient.cancelQueries({ queryKey: notesKey });

      const previousNotes = queryClient.getQueryData(notesKey);

      queryClient.setQueryData(notesKey, (current) =>
        current
          ? {
              ...current,
              notes: current.notes?.filter((note) => note.id !== noteId) ?? [],
            }
          : current,
      );

      return { previousNotes, relationshipMappingId };
    },
    onSuccess: () => {
      showSuccess("Note deleted successfully!");
      action?.();
    },
    onError: (error, _variables, context) => {
      if (context?.relationshipMappingId) {
        queryClient.setQueryData(
          relationshipNotesKey(context.relationshipMappingId),
          context.previousNotes,
        );
      }

      showErrorFromMutation(error, error.message || "Failed to delete note");
    },
    onSettled: (_data, _error, variables) => {
      if (!variables?.relationshipMappingId) return;

      queryClient.invalidateQueries({
        queryKey: relationshipNotesKey(variables.relationshipMappingId),
      });
    },
  });
};
  

/** Follow up reminder */
export const useFetchFollowUpReminder = ({
  relationshipMappingId = "",
}) => {
  return useQuery({
    queryKey: followUpReminderKey(relationshipMappingId),
    queryFn: () => fetchFollowUpReminderApi({ relationshipMappingId }),
    enabled: !!relationshipMappingId,
  });
};


// add follow up reminder
export const useAddFollowUpReminder = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ relationshipMappingId, followUpReminder }) =>
      addFollowUpReminderApi({ relationshipMappingId, followUpReminder }),
    onSuccess: (_data, { relationshipMappingId }) => {
      showSuccess("Follow up reminder added successfully!");
      queryClient.invalidateQueries({
        queryKey: followUpReminderKey(relationshipMappingId),
      });
      action?.();
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to add follow up reminder");
    },
  });
};

// update follow up reminder
export const useUpdateFollowUpReminder = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ followUpId, followUpReminder }) =>
      updateFollowUpReminderApi({ followUpId, followUpReminder }),
    onSuccess: (_data, { relationshipMappingId }) => {
      showSuccess("Follow up reminder updated successfully!");
      queryClient.invalidateQueries({
        queryKey: followUpReminderKey(relationshipMappingId),
      });
      action?.();
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to update follow up reminder",
      );
    },
  });
};

// delete follow up reminder
export const useDeleteFollowUpReminder = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ followUpId }) =>
      deleteFollowUpReminderApi({ followUpId }),

    onMutate: async ({ followUpId, relationshipMappingId }) => {
      const reminderKey = followUpReminderKey(relationshipMappingId);

      await queryClient.cancelQueries({ queryKey: reminderKey });

      const previousFollowUps = queryClient.getQueryData(reminderKey);

      queryClient.setQueryData(reminderKey, (current) =>
        current
          ? {
              ...current,
              follow_ups:
                current.follow_ups?.filter((item) => item.id !== followUpId) ??
                [],
            }
          : current,
      );

      return { previousFollowUps, relationshipMappingId };
    },
    onSuccess: () => {
      showSuccess("Follow up reminder deleted successfully!");
      action?.();
    },
    onError: (error, _variables, context) => {
      if (context?.relationshipMappingId) {
        queryClient.setQueryData(
          followUpReminderKey(context.relationshipMappingId),
          context.previousFollowUps,
        );
      }

      showErrorFromMutation(
        error,
        error.message || "Failed to delete follow up reminder",
      );
    },
    onSettled: (_data, _error, variables) => {
      if (!variables?.relationshipMappingId) return;

      queryClient.invalidateQueries({
        queryKey: followUpReminderKey(variables.relationshipMappingId),
      });
    },
  });
};

// move contact to campaign
export const useMoveContactToCampaign = ({ action } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => moveContactToCampaignApi(payload),
    onSuccess: (data, { relationshipMappingId }) => {
      if (!data?.success) {
        showError(
          data?.result?.message || "Failed to enroll contact in campaign",
        );
        return;
      }

      if (data?.result?.status === "skipped") {
        showSuccess("Contact is already enrolled in this campaign.");
      } else {
        showSuccess("Contact enrolled in campaign successfully!");
      }

      queryClient.invalidateQueries({
        queryKey: relationshipDetailKey(relationshipMappingId),
      });
      action?.();
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to enroll contact in campaign",
      );
    },
  });
};
